import { useCallback, useMemo } from "react"
import { toUpper } from "lodash"
import { useDispatch } from "react-redux"
import { Creators } from "model/apiWrapper"
import useResource from "hooks/resources"
import { useAccountAlias } from "resources/account"
import { useMutation } from "@tanstack/react-query"
import { Creators as modelCreators } from "model/data"
import { RESOURCE_LIST_QUERY } from "resources/resource"
import { OK } from "helpers/data-statuses"
import { useNotifications } from "hooks/notifications"
import { PermissionLevel } from "helpers/permissions"

const RESOURCE_TYPE = "template"
const BASE_COLLECTIONS_TO_RESET = [`resourcesList-${RESOURCE_TYPE}`]

export type TemplateType = "asset-view" | "dashboard-custom-graph"

export interface TemplateResponse {
  id: string
  name: string
  templateType?: TemplateType
  permissionLevel?: PermissionLevel
  config: {
    metrics: string[]
  }
}

export interface SaveTemplateInput {
  id?: string
  type?: TemplateType
  name: string
  metrics: string[]
}

export interface DeleteTemplateInput {
  id: string
}

const LEGACY_UNTYPED_TEMPLATE_DEFAULT_TYPE = "asset-view"

function isLegacyUntypedTemplate(template: TemplateResponse): boolean {
  return template.templateType == null
}

export function matchesTemplateType(template: TemplateResponse, templateType: TemplateType): boolean {
  if (templateType === LEGACY_UNTYPED_TEMPLATE_DEFAULT_TYPE && isLegacyUntypedTemplate(template)) return true
  return template.templateType === templateType
}

function getCollectionsToResetForTemplate(templateId?: string | null): string[] {
  if (!templateId) return BASE_COLLECTIONS_TO_RESET
  return [...BASE_COLLECTIONS_TO_RESET, `templateMetadata.${templateId}`]
}

function filterMixedTemplateTypesClientSide(
  untypedTemplates: TemplateResponse[] | null | undefined,
  templateType: TemplateType,
): TemplateResponse[] | undefined {
  return untypedTemplates?.filter((template: TemplateResponse) => matchesTemplateType(template, templateType))
}

export function useTemplates(templateType?: TemplateType) {
  const resourceKey = `resourcesList-${RESOURCE_TYPE}`
  const [untypedTemplates, status] = useResource<TemplateResponse[]>({
    resource: resourceKey,
    url: () => ({
      id: resourceKey,
      query: RESOURCE_LIST_QUERY,
      variables: { resourceType: toUpper(RESOURCE_TYPE) },
    }),
  }, "resources")

  const filteredTemplates = useMemo(() => {
    if (!templateType || status !== OK) return untypedTemplates
    return filterMixedTemplateTypesClientSide(untypedTemplates, templateType)
  }, [untypedTemplates, templateType, status])

  return [filteredTemplates, status]
}

export function useTemplateMetadata(templateId?: string) {
  const account = useAccountAlias()
  const resource = `templateMetadata.${templateId}`

  return useResource<TemplateResponse>({
    resource,
    url: () => (account && templateId ? `/api/${account}/templates/${templateId}` : null),
  }, resource)
}

// Targeted retries for the fatal GraphErrorFallback paths — resetCollections clears the cached
// resource entry so useResource's effect refetches, without a full window.location.reload().
export function useRetryTemplatesList() {
  const dispatch = useDispatch()
  return useCallback(() => {
    dispatch(modelCreators.resetCollections(BASE_COLLECTIONS_TO_RESET))
  }, [dispatch])
}

export function useRetryTemplateMetadata(templateId?: string | null) {
  const dispatch = useDispatch()
  return useCallback(() => {
    if (!templateId) return
    dispatch(modelCreators.resetCollections([`templateMetadata.${templateId}`]))
  }, [dispatch, templateId])
}

export function useSaveTemplate() {
  const account = useAccountAlias()
  const dispatch = useDispatch()
  const notifications = useNotifications()

  const mutation = useMutation<TemplateResponse, Error, SaveTemplateInput>({
    mutationFn: (template) => {
      const typeFieldIfProvided = template.type ? { type: template.type } : {}
      const payload = {
        name: template.name,
        config: { metrics: template.metrics },
        ...typeFieldIfProvided,
      }

      return new Promise((resolve, reject) => {
        if (template.id) {
          dispatch(Creators.put(`/api/${account}/templates/${template.id}`, payload, resolve, reject))
        } else {
          dispatch(Creators.post(`/api/${account}/templates`, payload, resolve, reject))
        }
      })
    },
    onSuccess: (data, template) => {
      const templateId = data?.id ?? template?.id
      dispatch(modelCreators.resetCollections(getCollectionsToResetForTemplate(templateId)))
    },
    onError: () => {
      notifications.error("Couldn't save this view. Please try again.")
    },
  })

  return mutation.mutateAsync
}

export function useDeleteTemplate() {
  const account = useAccountAlias()
  const dispatch = useDispatch()
  const notifications = useNotifications()

  const mutation = useMutation<unknown, Error, DeleteTemplateInput>({
    mutationFn: (deletedTemplate) => {
      return new Promise<void>((resolve, reject) => {
        dispatch(Creators.delete(`/api/${account}/templates/${deletedTemplate.id}`, undefined, resolve, reject))
      })
    },
    onSuccess: (_data, deletedTemplate) => {
      const templateId = deletedTemplate.id
      dispatch(modelCreators.resetCollections(getCollectionsToResetForTemplate(templateId)))
    },
    onError: () => {
      notifications.error("Couldn't delete this view. Please try again.")
    },
  })

  return mutation.mutateAsync
}
